import { useQuery } from '@tanstack/react-query';
import { fetchHealth, queryKeys } from './api';
import { IS_REVIEW_BUILD } from './reviewMode';

/**
 * Reads GET /health and reports it honestly: three distinct states, never a
 * hopeful "ok" while the request is still in flight or has failed.
 *
 * A review build has no server, so it says THAT rather than showing a red dot for
 * a condition nobody is meant to fix.
 */
/**
 * `card` is a standalone block (the phone's Account sheet, unchanged until the phone round, P6).
 * `inline` is the desktop sidebar's server line, and since 2026-10-04 it is there ONLY while the
 * server does not answer (redesign register R-S1: "Server status line only when the server does
 * not answer, in words, visible without hover"). A line that says all is well on every screen is
 * noise a person learns to read past; the one that matters has to be read.
 */
export type ApiStatusVariant = 'card' | 'inline';

export function ApiStatus({ variant = 'card' }: { readonly variant?: ApiStatusVariant }) {
  const health = useQuery({
    queryKey: queryKeys.health(),
    queryFn: ({ signal }) => fetchHealth(signal),
    retry: false,
    staleTime: 10_000,
    enabled: !IS_REVIEW_BUILD,
  });

  const [dotClass, label, detail] = IS_REVIEW_BUILD
    ? ['bg-info', 'Fixture data', 'design review build, no API']
    : health.isPending
      ? ['bg-line-strong animate-pulse', 'Checking API', null]
      : health.isError
        ? ['bg-danger', 'API unreachable', health.error.message]
        : ['bg-success', 'API reachable', `version ${health.data.version}`];

  if (variant === 'inline') {
    /**
     * The review build has no server and is answered by its fixture, so it draws nothing here;
     * the line's own look is the specimen on `/dev/shell-states`.
     */
    if (IS_REVIEW_BUILD || !health.isError) return null;
    return <ServerLine detail={health.error.message} />;
  }

  return (
    <div
      className="flex items-center gap-3 rounded-surface border border-line bg-surface px-4 py-3"
      aria-live="polite"
    >
      <span className={`size-2.5 shrink-0 rounded-full ${dotClass}`} aria-hidden="true" />
      <div className="min-w-0">
        <p className="text-sm font-medium text-ink">{label}</p>
        {detail !== null && (
          <p className="truncate font-mono text-xs text-ink-muted" title={detail}>
            {detail}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * THE WORDS ARE PLAIN (coordinator decision Q-NAV-2: "The server is not answering.", register
 * R56: no system words), and the detail that was a tooltip (`title`) is a second line, because a
 * tooltip is hover-only (L11). The detail is the request's own error, which is what somebody
 * fixing the server needs to read off a screenshot.
 */
export const SERVER_NOT_ANSWERING = 'The server is not answering.';

export function ServerLine({ detail }: { readonly detail: string | null }) {
  return (
    <div
      // `px-3` is --nav-item-inset's partner: the dot lines up with the row icons above it.
      className="flex items-start gap-2 px-3 text-2xs text-ink-muted"
      role="status"
      aria-live="polite"
    >
      <span className="mt-1.5 size-2 shrink-0 rounded-full bg-danger" aria-hidden="true" />
      <div className="min-w-0">
        <p>{SERVER_NOT_ANSWERING}</p>
        {detail !== null && <p className="break-words">{detail}</p>}
      </div>
    </div>
  );
}
